import { Button } from '@ironhack/design-system2/components/button';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Text } from '@ironhack/design-system2/components/text';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import { type ReactElement } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';

import { getButtonIconProps } from '@/app/components/client/button';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { StructuredContent } from '@/app/components/client/structured-content';
import { CmsImage } from '@/app/components/server/cms-image';
import { Tag, type TagProps } from '@/app/components/server/tag';
import { type DatoButtonBlock, type DatoImage } from '@/lib/datocms/types';

export type StartScreenData = {
  color: string;
  image: DatoImage | null;
  legalText: StructuredTextGraphQlResponse;
  startButton: DatoButtonBlock;
  subtitle: StructuredTextGraphQlResponse;
  timeEstimateTag: TagProps | null;
  title: string;
};

type Props = {
  data: StartScreenData;
  onClick?: () => void;
} & Omit<FlexProps, 'onClick'>;

export const StartScreen = (props: Props): ReactElement => {
  const {
    color,
    data: {
      image,
      legalText,
      startButton: {
        text: buttonText,
        icon: buttonIcon,
        variant: buttonVariant,
      },
      subtitle,
      timeEstimateTag,
      title,
    },
    onClick,
    ...flexProps
  } = props;

  const buttonIconProps = getButtonIconProps(buttonIcon?.[0]);

  return (
    <ModuleWrapper
      innerProps={{
        align: 'center',
        color,
        direction: 'column',
        gap: 4,
        textAlign: 'center',
        ...flexProps,
      }}
      moduleFormat={{
        background: 'transparent',
        spacingBottom: '24',
        spacingTop: '24',
      }}
    >
      <Flex align="center" direction="column">
        <Text as="h1" textStyle={['3xl', null, '4xl']}>
          {title}
        </Text>
        {!isEmptyDocument(subtitle) && (
          <StructuredContent data={subtitle} mt={1} />
        )}
        {timeEstimateTag && (
          <Tag
            gap={1}
            iconProps={{
              boxSize: '24px',
              verticalAlign: 'middle',
            }}
            mt={3}
            px={2}
            textStyle="l"
            {...timeEstimateTag}
          />
        )}
      </Flex>
      {image && <CmsImage data={image.responsiveImage} />}

      <Flex align="center" direction="column" gap={2}>
        <Button
          onClick={onClick}
          variant={buttonVariant}
          width={['100%', null, 'auto']}
          {...buttonIconProps}
        >
          {buttonText}
        </Button>
        {!isEmptyDocument(legalText) && (
          <StructuredContent
            color="text.secondary"
            data={legalText}
            textStyle="m"
          />
        )}
      </Flex>
    </ModuleWrapper>
  );
};

export const startScreenFragment = `
  fragment startScreenFragment on QuizStartScreenRecord {
    image {
      responsiveImage(
        imgixParams: {
          auto: format
          cornerRadius: "8"
          fit: crop
          h: 267
          mask: corners
          q: 45
          w: 400
        }
      ) {
        ...responsiveImageFragment
      }
    }
    legalText {
      value
    }
    startButton {
      ...buttonFragment
    }
    subtitle {
      value
    }
    timeEstimateTag {
      ...tagFragment
    }
    title
  }
`;
